import { useState } from "react";
import { ChevronDown, ChevronRight, Calculator, Ship, Anchor, Thermometer, Snowflake, Fuel, Zap, Database, MapPin, Scale } from "lucide-react";

const FuelEUCalculations = () => {
  const [openSections, setOpenSections] = useState(false);

  const toggleSection = () => {
    setOpenSections((prev) => !prev);
  };

  const coreCalculations = [
    {
      id: "fuel-properties-sourcing",
      title: "Fuel Properties Sourcing",
      description: "Retrieves comprehensive fuel data from FuelTable database for HFO, LFO, MGO",
      formula: "Fuel Properties from FuelTable collection",
      subFormulas: [
        "Required fuels: HFO, LFO, MGO",
        "Key fields: co2eqWtT, CF_C02, CF_CH4, CF_N20, CSlip, LCV",
        "Fuel classification: RFNBO → RWD=2.0, else RWD=1.0",
        "Validation: All three fuel types must exist"
      ],
      icon: <Database className="w-5 h-5" />,
      borderColor: "border-blue-200",
      bgColor: "bg-blue-50",
      textColor: "text-blue-700",
    },
    {
      id: "ice-class-adjustments",
      title: "Ice Class Fuel Adjustments",
      description: "Complex fuel mass reduction for ice-class vessels based on ice conditions",
      formula: "Adjusted Mass = Original Mass - (E_additional_ice / LCV)",
      subFormulas: [
        "Only if iceClassShip = 'YES'",
        "E_additional_ice = E_ice_class + E_ice_conditions",
        "IA/IA Super: +5% additional energy",
        "Mass reduction proportional to actual ice condition usage"
      ],
      icon: <Snowflake className="w-5 h-5" />,
      borderColor: "border-cyan-200",
      bgColor: "bg-cyan-50",
      textColor: "text-cyan-700",
    },
    {
      id: "well-to-tank-intensity",
      title: "Well-to-Tank (WtT) Intensity",
      description: "Calculates upstream GHG intensity from fuel production and transport",
      formula: "WtT = Σ(Fuel × CO₂eq_WtT × LCV) ÷ Total Energy",
      subFormulas: [
        "Fuel mass converted to grams: kg × 1,000,000",
        "CO₂eq_WtT from FuelTable (g CO₂e/MJ)",
        "Total Energy includes shore power",
        "Units: g CO₂e/MJ"
      ],
      icon: <Fuel className="w-5 h-5" />,
      borderColor: "border-green-200",
      bgColor: "bg-green-50",
      textColor: "text-green-700",
    },
    {
      id: "tank-to-wake-intensity",
      title: "Tank-to-Wake (TtW) Intensity",
      description: "Calculates combustion GHG intensity including methane slip",
      formula: "TtW = Σ[Fuel × ((1-CSlip)×CO₂eq_TtW + CSlip×28)] ÷ Total Energy",
      subFormulas: [
        "CO₂eq_TtW = (CF_CO₂×1) + (CF_CH₄×28) + (CF_N₂O×265)",
        "CSlip = Carbon slip percentage from FuelTable",
        "28 = GWP of methane (CH₄)",
        "Accounts for unburned methane emissions"
      ],
      icon: <Zap className="w-5 h-5" />,
      borderColor: "border-amber-200",
      bgColor: "bg-amber-50",
      textColor: "text-amber-700",
    },
    {
      id: "well-to-wake-intensity",
      title: "Well-to-Wake (WtW) Intensity",
      description: "Total lifecycle GHG intensity including renewable fuel rewards",
      formula: "WtW = Reward Factor × (WtT + TtW)",
      subFormulas: [
        "Reward Factor Wind (default: 1.0)",
        "Sum of upstream and combustion intensities",
        "Final GHG intensity in g CO₂e/MJ",
        "Compared against annual targets"
      ],
      icon: <Thermometer className="w-5 h-5" />,
      borderColor: "border-red-200",
      bgColor: "bg-red-50",
      textColor: "text-red-700",
    },
    {
      id: "ghg-intensity-target",
      title: "GHG Intensity Target",
      description: "Annual compliance targets based on baseline reduction schedule",
      formula: "Target = 91.16 × (1 - Reduction Factor)",
      subFormulas: [
        "Baseline: 91.16 g CO₂e/MJ",
        "2025: 2% reduction → 89.34 g CO₂e/MJ",
        "2035: 14.5% reduction → 77.94 g CO₂e/MJ",
        "2050: 80% reduction → 18.23 g CO₂e/MJ"
      ],
      icon: <Scale className="w-5 h-5" />,
      borderColor: "border-purple-200",
      bgColor: "bg-purple-50",
      textColor: "text-purple-700",
    }
  ];

  const segmentClassification = [
    {
      id: "vessel-state-segmentation",
      title: "Vessel State Segmentation",
      description: "Classifies fuel consumption by vessel operational state and location",
      formula: "Segment = f(vesselStateTransition, portLocation)",
      subFormulas: [
        "In Port States: 'In Port', 'Maneuvering (anchored)'",
        "At Sea States: 'At Sea', 'At Sea (Drifting)', 'Maneuvering'",
        "EEA Ports: from Port collection (eea = true)",
        "Transhipment ports for container vessels"
      ],
      icon: <Ship className="w-5 h-5" />,
      borderColor: "border-indigo-200",
      bgColor: "bg-indigo-50",
      textColor: "text-indigo-700",
    },
    {
      id: "transhipment-logic",
      title: "Transhipment Port Logic",
      description: "Special handling for container vessel transhipment operations",
      formula: "Transhipment Ports = {'Port Said East', 'Tanger Med'}",
      subFormulas: [
        "Only for container vessels (vesselType = 'Container ships')",
        "Additional segments: nonEEAToTranshipment, transhipmentToEEA, etc.",
        "Treated as EEA ports for segmentation",
        "Excluded from standard EEA port list"
      ],
      icon: <MapPin className="w-5 h-5" />,
      borderColor: "border-orange-200",
      bgColor: "bg-orange-50",
      textColor: "text-orange-700",
    },
    {
      id: "segment-types",
      title: "Segment Types",
      description: "Eight distinct segments for FuelEU compliance calculations",
      formula: "REQUIRED_SEGMENTS = [at-eea, between, departed, arrived, transhipment...]",
      subFormulas: [
        "at-eea: At EEA Ports (vessel in port)",
        "between: Between EEA Ports",
        "departed: Departed EEA → Non-EEA",
        "arrived: Arrived Non-EEA → EEA",
        "+ 4 transhipment segments"
      ],
      icon: <Anchor className="w-5 h-5" />,
      borderColor: "border-teal-200",
      bgColor: "bg-teal-50",
      textColor: "text-teal-700",
    }
  ];

  const complianceCalculations = [
    {
      id: "compliance-balance",
      title: "Compliance Balance",
      description: "Calculates surplus or deficit relative to GHG intensity target",
      formula: "Balance = (Target - Actual) × Total Energy",
      subFormulas: [
        "Positive = Surplus (compliant)",
        "Negative = Deficit (non-compliant)",
        "Total Energy in MJ",
        "Units: g CO₂ equivalent"
      ],
      icon: <Calculator className="w-5 h-5" />,
      borderColor: "border-emerald-200",
      bgColor: "bg-emerald-50",
      textColor: "text-emerald-700",
    },
    {
      id: "penalty-calculation",
      title: "Penalty Calculation",
      description: "Calculates financial penalties for non-compliance",
      formula: "Penalty = (Deficit ÷ (WtW × 41,000)) × 2,400 × Escalation",
      subFormulas: [
        "Base penalty: €2,400 per ton CO₂e",
        "41,000 MJ = reference energy",
        "Escalation: +10% per consecutive deficient year",
        "Applied per segment"
      ],
      icon: <Calculator className="w-5 h-5" />,
      borderColor: "border-rose-200",
      bgColor: "bg-rose-50",
      textColor: "text-rose-700",
    },
    {
      id: "total-energy-calculation",
      title: "Total Energy Calculation",
      description: "Calculates total energy used including RFNBO rewards",
      formula: "Energy = Σ(Fuel × LCV × RWD) + Shore Power",
      subFormulas: [
        "LCV = Lower Calorific Value from FuelTable",
        "RWD = 2.0 for RFNBO fuels, 1.0 otherwise",
        "Shore power from FuelEUData collection",
        "All calculations in MJ"
      ],
      icon: <Zap className="w-5 h-5" />,
      borderColor: "border-yellow-200",
      bgColor: "bg-yellow-50",
      textColor: "text-yellow-700",
    }
  ];

  const reductionSchedule = {
    baseline: 91.16,
    targets: [
      { year: 2025, reduction: 0.02, target: 89.34 },
      { year: 2030, reduction: 0.06, target: 85.69 },
      { year: 2035, reduction: 0.145, target: 77.94 },
      { year: 2040, reduction: 0.31, target: 62.90 },
      { year: 2045, reduction: 0.62, target: 34.64 },
      { year: 2050, reduction: 0.80, target: 18.23 }
    ]
  };

  const dataSources = [
    {
      collection: "FuelTable",
      fields: ["co2eqWtT", "CF_C02", "CF_CH4", "CF_N20", "CSlip", "LCV", "Fuel_Class"],
      purpose: "Fuel properties and emission factors"
    },
    {
      collection: "FuelEUData",
      fields: ["shorePowerEnergy", "totalDistance", "distTravelledInIceConditions", "massInIceConditions*", "timeSpentInEU"],
      purpose: "Annual operational data"
    },
    {
      collection: "VesselPerformance",
      fields: ["fromPort", "toPort", "heavyFuelOil", "lightFuelOil", "marineGasOil", "vesselStateTransition"],
      purpose: "Voyage fuel consumption and state"
    },
    {
      collection: "Port",
      fields: ["portName", "portCode", "eea"],
      purpose: "EEA port identification"
    },
    {
      collection: "Vessel",
      fields: ["vesselType", "iceClassShip", "iceClassType", "rewardFactorWind", "howManyYearsComplDeficient"],
      purpose: "Vessel configuration and status"
    }
  ];

  const iceClassAdjustmentSteps = [
    "Calculate energy in ice conditions from fuel consumption fields if it is in Sailing in Ice Condition",
    "Calculate energy in open water (total - ice)",
    "Adjust ice condition energy based on distance ratio",
    "Calculate additional energy due to ice conditions",
    "Add ice class energy for IA/IA Super vessels",
    "Apply cap: E_additional ≤ 1.3 × E_open_water",
    "Distribute energy reduction to fuels based on ice usage"
  ];

  return (
    <div>
      <div className="mb-6 border border-gray-300 rounded-lg overflow-hidden">
        <button
          className="flex items-center justify-between w-full p-5 bg-gray-50 hover:bg-gray-100 transition-colors"
          onClick={toggleSection}
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-[#c5fcdd] rounded-lg">
              <Calculator className="w-6 h-6 text-[#27ae60]" />
            </div>
            <div className="text-left">
              <h2 className="text-md font-bold text-gray-800">
                FuelEU Compliance Calculations
              </h2>
              <p className="text-gray-600 text-sm">
                Comprehensive GHG intensity, ice class adjustments, and compliance methodology
              </p>
            </div>
          </div>
          {openSections ? (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-500" />
          )}
        </button>

        {openSections && (
          <div className="p-5 bg-white border-t border-gray-200">
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Data Sources</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                {dataSources.map((source, index) => (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h4 className="font-semibold text-gray-800 mb-2">{source.collection}</h4>
                    <div className="text-sm text-gray-600 mb-2">{source.purpose}</div>
                    <div className="text-xs text-gray-500 space-y-1">
                      {source.fields.map((field, idx) => (
                        <div key={idx} className="font-mono">{field}</div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">GHG Intensity Reduction Schedule</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {reductionSchedule.targets.map((target, index) => (
                    <div key={index} className="bg-white p-3 rounded border">
                      <div className="font-semibold text-gray-800">Year {target.year}</div>
                      <div className="text-sm text-gray-600">Reduction: {(target.reduction * 100).toFixed(1)}%</div>
                      <div className="text-sm font-mono text-green-600">Target: {target.target} g CO₂e/MJ</div>
                      <div className="text-xs text-gray-500">From baseline: {reductionSchedule.baseline} g CO₂e/MJ</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Ice Class Adjustment Steps</h3>
              <div className="bg-cyan-50 p-4 rounded-lg border border-cyan-200">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {iceClassAdjustmentSteps.map((step, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <div className="mt-1">
                        <div className="w-2 h-2 bg-cyan-500 rounded-full"></div>
                      </div>
                      <span className="text-sm text-cyan-800">{step}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Core Calculations</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {coreCalculations.map((calc) => (
                  <div
                    key={calc.id}
                    className={`p-5 rounded-lg border ${calc.borderColor} ${calc.bgColor}`}
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <div className={`p-2 rounded-lg ${calc.textColor} bg-white`}>
                        {calc.icon}
                      </div>
                      <div>
                        <h4 className={`font-bold text-gray-800 mb-1 ${calc.textColor}`}>
                          {calc.title}
                        </h4>
                        <p className="text-sm text-gray-600 mb-4">{calc.description}</p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-1">Formula:</p>
                        <p className="text-sm font-semibold bg-white p-3 rounded border border-gray-200 font-mono">
                          {calc.formula}
                        </p>
                      </div>
                      
                      {calc.subFormulas && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">Details:</p>
                          <div className="space-y-2">
                            {calc.subFormulas.map((subFormula, idx) => (
                              <div key={idx} className="text-sm bg-white p-2 rounded border border-gray-100 font-mono">
                                {subFormula}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Segment Classification</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {segmentClassification.map((calc) => (
                  <div
                    key={calc.id}
                    className={`p-5 rounded-lg border ${calc.borderColor} ${calc.bgColor}`}
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <div className={`p-2 rounded-lg ${calc.textColor} bg-white`}>
                        {calc.icon}
                      </div>
                      <div>
                        <h4 className={`font-bold text-gray-800 mb-1 ${calc.textColor}`}>
                          {calc.title}
                        </h4>
                        <p className="text-sm text-gray-600 mb-4">{calc.description}</p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-1">Logic:</p>
                        <p className="text-sm font-semibold bg-white p-3 rounded border border-gray-200 font-mono">
                          {calc.formula}
                        </p>
                      </div>
                      
                      {calc.subFormulas && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">Implementation:</p>
                          <div className="space-y-2">
                            {calc.subFormulas.map((subFormula, idx) => (
                              <div key={idx} className="text-sm bg-white p-2 rounded border border-gray-100 font-mono">
                                {subFormula}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Compliance Calculations</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {complianceCalculations.map((calc) => (
                  <div
                    key={calc.id}
                    className={`p-5 rounded-lg border ${calc.borderColor} ${calc.bgColor}`}
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <div className={`p-2 rounded-lg ${calc.textColor} bg-white`}>
                        {calc.icon}
                      </div>
                      <div>
                        <h4 className={`font-bold text-gray-800 mb-1 ${calc.textColor}`}>
                          {calc.title}
                        </h4>
                        <p className="text-sm text-gray-600 mb-4">{calc.description}</p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-1">Calculation:</p>
                        <p className="text-sm font-semibold bg-white p-3 rounded border border-gray-200 font-mono">
                          {calc.formula}
                        </p>
                      </div>
                      
                      {calc.subFormulas && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">Details:</p>
                          <div className="space-y-2">
                            {calc.subFormulas.map((subFormula, idx) => (
                              <div key={idx} className="text-sm bg-white p-2 rounded border border-gray-100 font-mono">
                                {subFormula}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-8 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
              <h4 className="font-semibold text-emerald-800 mb-2">Complete Compliance Calculation</h4>
              <div className="text-sm text-emerald-700 space-y-2">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-white p-3 rounded border">
                    <div className="font-medium">Step 1: WtW Intensity</div>
                    <div className="text-xs font-mono mt-1">Reward × (WtT + TtW)</div>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <div className="font-medium">Step 2: Compare to Target</div>
                    <div className="text-xs font-mono mt-1">Target - Actual</div>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <div className="font-medium">Step 3: Compliance Balance</div>
                    <div className="text-xs font-mono mt-1">(Target - Actual) × Energy</div>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <div className="font-medium">Step 4: Penalty</div>
                    <div className="text-xs font-mono mt-1">(Deficit ÷ (WtW×41k)) × 2,400</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FuelEUCalculations;